/**
 * Product Gallery Custom Element
 * Varyant bazlı galeri güncellemesi için custom element
 *
 * IMPORTANT - APP GEÇİŞİ İÇİN:
 * Şu anda variant-swatch-king app'i kullanılıyor.
 * App kaldırıldığında:
 * 1. #listenToUrlChanges() metodunu devre dışı bırakın
 * 2. #listenToVariantEvents() metodunu aktif hale getirin
 * 3. Kendi variant selector'ınızdan 'variant:change' event'i dispatch edin
 *
 * Örnek event dispatch (app kaldırıldıktan sonra):
 * document.dispatchEvent(new CustomEvent('variant:change', {
 *   detail: { variantId: SELECTED_VARIANT_ID }
 * }));
 */

class ProductGallery extends HTMLElement {
  constructor() {
    super();
    this.currentVariantId = this.dataset.variantId;
    this.isUpdating = false;
    this.sectionId = this.#getSectionId();
  }

  connectedCallback() {
    this.#setupEventListeners();
    // Slider render olduktan sonra Fancybox'ı başlat
    requestAnimationFrame(() => {
      this.#initFancybox();
    });
  }

  disconnectedCallback() {
    this.#removeEventListeners();
    this.#destroyFancybox();
  }

  #setupEventListeners() {
    // APP MEVCUT: URL değişimini dinle
    this.#listenToUrlChanges();

    // APP KALDIRILINCA: Bu metodu aktif et
    // this.#listenToVariantEvents();
  }

  #removeEventListeners() {
    if (this.urlChangeHandler) {
      window.removeEventListener('popstate', this.urlChangeHandler);
      document.removeEventListener('click', this.clickHandler);
    }

    if (this.variantChangeHandler) {
      document.removeEventListener('variant:change', this.variantChangeHandler);
    }
  }

  /**
   * URL değişimlerini dinle (variant-swatch-king app'i için)
   * APP MEVCUT: Bu metod aktif
   */
  #listenToUrlChanges() {
    this.urlChangeHandler = () => {
      const variantId = this.#getVariantIdFromUrl();
      if (variantId && variantId !== this.currentVariantId) {
        this.#updateGallery(variantId);
      }
    };

    this.clickHandler = (e) => {
      const swatchItem = e.target.closest('.swatch-view-item');
      if (!swatchItem) return;

      // Kısa bir gecikme ile URL'den variant ID'yi al
      setTimeout(() => {
        const variantId = this.#getVariantIdFromUrl();
        if (variantId && variantId !== this.currentVariantId) {
          this.#updateGallery(variantId);
        }
      }, 50);
    };

    window.addEventListener('popstate', this.urlChangeHandler);
    document.addEventListener('click', this.clickHandler);
  }

  /**
   * Custom variant change event'ini dinle
   * APP KALDIRILINCA: Bu metodu aktif et ve #listenToUrlChanges()'i kaldır
   */
  #listenToVariantEvents() {
    this.variantChangeHandler = (e) => {
      const variantId = e.detail?.variantId;
      if (variantId && variantId !== this.currentVariantId) {
        this.#updateGallery(variantId);
      }
    };

    document.addEventListener('variant:change', this.variantChangeHandler);
  }

  /**
   * URL'den variant ID'yi parse et
   */
  #getVariantIdFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const variantId = params.get('variant');
    return variantId ? String(variantId) : null;
  }

  /**
   * Section ID'yi bul (product-template section'dan)
   */
  #getSectionId() {
    const section = this.closest('[data-section-id]');
    return section ? section.dataset.sectionId : '';
  }

  /**
   * Ana gallery güncelleme metodu
   */
  async #updateGallery(variantId) {
    if (this.isUpdating) return;

    this.isUpdating = true;
    this.currentVariantId = variantId;

    try {
      this.#showSkeleton();

      // Section Rendering API ile gallery'yi fetch et
      const url = `${window.location.pathname}?variant=${variantId}&section_id=${this.sectionId}`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error('Gallery fetch failed');
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Yeni gallery'yi bul
      const newGallery = doc.querySelector('n-product-gallery');

      if (newGallery) {
        // Mevcut scroll pozisyonunu kaydet
        const thumbnailScroller = this.querySelector(`#thumbnailScroller-${this.sectionId}`);
        const scrollTop = thumbnailScroller ? thumbnailScroller.scrollTop : 0;

        // Gallery'yi güncelle
        this.innerHTML = newGallery.innerHTML;
        this.dataset.variantId = variantId;

        // Slider'ı yeniden başlat (n-responsive-slider custom element'i otomatik başlatacak)
        this.#reinitializeSlider();

        // Scroll pozisyonunu geri yükle
        const newScroller = this.querySelector(`#thumbnailScroller-${this.sectionId}`);
        if (newScroller) {
          newScroller.scrollTop = scrollTop;
        }

        // Fancybox'ı yeniden başlat
        this.#destroyFancybox();
        requestAnimationFrame(() => {
          this.#initFancybox();
        });

        this.#hideSkeleton();
      }
    } catch (error) {
      console.error('Gallery update error:', error);
      this.#hideSkeleton();
    } finally {
      this.isUpdating = false;
    }
  }

  /**
   * Slider'ı yeniden başlat
   */
  #reinitializeSlider() {
    const slider = this.querySelector('n-responsive-slider');
    if (slider && slider.connectedCallback) {
      slider.connectedCallback();
    }
  }

  /**
   * Skeleton loading göster
   */
  #showSkeleton() {
    this.classList.add('product-gallery--loading');
  }

  /**
   * Skeleton loading gizle
   */
  #hideSkeleton() {
    this.classList.remove('product-gallery--loading');
  }

  /**
   * Fancybox'ı başlat
   */
  #initFancybox() {
    // Eğer Fancybox yüklenmemişse, bekle
    if (typeof Fancybox === 'undefined') {
      setTimeout(() => this.#initFancybox(), 100);
      return;
    }

    // Gallery içindeki tüm fancybox elementlerini bul
    const fancyboxElements = this.querySelectorAll('[data-fancybox="product-images"]');

    if (fancyboxElements.length === 0) return;

    // Fancybox'ı başlat
    this.fancyboxInstance = Fancybox.bind('[data-fancybox="product-images"]', {
      // Zoom efektini aktif tut
      zoomEffect: true,
      // Fade efekti
      fadeEffect: true,
      // Kapatma animasyonu
      hideScrollbar: true,
      // Drag to close
      dragToClose: true,
      // Ana toolbar ayarı - sadece close butonu
      Toolbar: {
        display: {
          left: [],
          middle: [],
          right: ['close']
        }
      },
      // Keyboard kısayolları
      keyboard: {
        Escape: 'close',
        Delete: 'close',
        Backspace: 'close',
        PageUp: 'next',
        PageDown: 'prev',
        ArrowUp: 'prev',
        ArrowDown: 'next',
        ArrowRight: 'next',
        ArrowLeft: 'prev',
      },
      // Carousel ayarları
      Carousel: {
        infinite: false,
        // Carousel toolbar'ı da aynı şekilde ayarla
        Toolbar: {
          display: {
            left: [],
            middle: [],
            right: ['close']
          }
        }
      },
      // Thumbnail'lar için özel ayarlar
      Thumbs: {
        type: 'classic',
        autoStart: true,
      },
    });
  }

  /**
   * Fancybox'ı yok et
   */
  #destroyFancybox() {
    // Eğer Fancybox yüklenmemişse, çık
    if (typeof Fancybox === 'undefined') return;

    // Açık olan tüm Fancybox instance'larını kapat
    const instances = Fancybox.getInstance();
    if (instances) {
      instances.close();
    }

    // Mevcut bind'ı temizle
    Fancybox.unbind('[data-fancybox="product-images"]');
    Fancybox.close();
  }
}

customElements.define('n-product-gallery', ProductGallery);
